/**
 * The decisions every input control makes about validation and phone entry, in one place.
 *
 * These were previously written inline in each editor branch — and inconsistently, which is how
 * several controls came to mark a field red without ever saying why, and how required fields came to
 * carry no asterisk until after a save was refused (bugs 5668, 5676, 5694, 5703).
 */

type ValidatedField = {
  /** mobx-react-form rules, e.g. "required|string". May be a string, an array, or absent. */
  rules?: string | string[] | null;
  error?: string | null;
};

/** Whether the field declares a required rule, and so should show its asterisk from the outset. */
export const isRequiredField = (field: ValidatedField | null | undefined): boolean => {
  const rules = field?.rules;
  if (!rules) return false;
  return Array.isArray(rules)
    ? rules.some((rule) => `${rule}`.includes("required"))
    : `${rules}`.includes("required");
};

/**
 * What to print beneath the control.
 *
 * The validation error wins over configured helper text: a static hint must never hide the reason a
 * save was refused. Falls back to a single space so the control keeps its height and the layout does
 * not jump when a message appears.
 */
export const fieldHelperText = (
  field: ValidatedField | null | undefined,
  helperText?: React.ReactNode,
): React.ReactNode => field?.error || helperText || " ";

/** Long enough for any international number with spacing, short enough to stop paste accidents. */
export const PHONE_MAX_LENGTH = 25;

/** Anything a phone number cannot contain: not a digit, plus, bracket, hyphen or space. */
const NOT_PHONE_CHARACTER = /[^0-9+()\-\s]/g;

/**
 * Keeps a phone number as the user typed it, minus characters it cannot contain.
 *
 * The contact phone used to be a dial-code widget. It reformatted on every keystroke, which swallowed
 * Backspace, Delete and Ctrl+A so a wrong number could not be corrected; it prefilled +64 whatever
 * the client's country; it read a local number such as 0211234567 as country code +02; and it
 * re-rendered a CSV-imported 0400111222 as "+04 001-112-22" and flagged it invalid (bugs 5671, 5675).
 *
 * Nothing in the specification asks for dial-code parsing — Mobile/Phone is optional free text
 * (S4.1), and Site Phone and Head Office Phone have always been plain fields. So the value is left
 * as entered: local, international and imported numbers all round-trip unchanged.
 *
 * A trailing space is deliberately preserved: stripping it mid-typing would fight the next keystroke.
 */
export const normalisePhoneInput = (value: string | null | undefined): string => {
  if (!value) return "";
  return `${value}`
    .replace(NOT_PHONE_CHARACTER, "")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/^\s+/, "")
    .slice(0, PHONE_MAX_LENGTH);
};
